import { FC, useEffect, useMemo, useRef, useState } from 'react';
import type { ProseChange, ReviewDecision, SpreadsheetChange } from '@libs/types';
import { Box, Button, Chip, IconButton, Paper, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Markdown } from '@components/Markdown';
import { RemediationDecision } from '@libs/constants';
import { type SingularKeys, useTrans } from '@libs/i18n';
import {
  CaretLeftIcon,
  CaretRightIcon,
  CheckCircleIcon,
  CheckIcon,
  QuotesIcon,
  SparkleIcon,
  XCircleIcon,
  XIcon,
} from '@phosphor-icons/react';

import { Pin } from './ReviewDocumentPanes';

export type AnyChange = ProseChange | SpreadsheetChange;

export const isProseChange = (c: AnyChange): c is ProseChange =>
  c.type === 'modification' || c.type === 'addition' || c.type === 'deletion';

export type ChangeFilter = 'all' | 'pending' | 'accepted' | 'rejected';

/**
 * Rows per page in the change list.
 *
 * A document with sixty changes made the rail a mile of scroll, and the rail
 * scrolls as one piece — so the list buried the focused change under it. Ten is
 * about a screen of rows in a rail this narrow, which keeps paging a deliberate
 * move rather than something a reviewer does every few changes.
 */
const PAGE_SIZE = 10;

/** Design's three change families, mapped onto the API's five change types. */
function changeTone(change: AnyChange): 'insert' | 'delete' | 'modify' {
  if (change.type === 'addition' || change.type === 'column_addition') return 'insert';
  if (change.type === 'deletion' || change.type === 'column_deletion') return 'delete';
  return 'modify';
}

/**
 * The focused change: what it is, which clause drove it, the before/after pair,
 * and the two buttons that decide it.
 */
export const FocusedChangePanel: FC<{
  change: AnyChange;
  index: number;
  hasPrev: boolean;
  hasNext: boolean;
  busy: boolean;
  onDecide: (decision: ReviewDecision) => void;
  onNav: (dir: 'prev' | 'next') => void;
}> = observer(({ change, index, hasPrev, hasNext, busy, onDecide, onNav }) => {
  const theme = useTheme();
  const { t } = useTrans();

  const tone = changeTone(change);
  const toneColor =
    tone === 'insert'
      ? theme.palette.green[700]
      : tone === 'delete'
        ? theme.palette.error.main
        : theme.palette.ocean[700];

  const accepted = change.decision === RemediationDecision.ACCEPTED;
  const rejected = change.decision === RemediationDecision.REJECTED;
  const prose = isProseChange(change);
  const primaryRef = change.references[0];

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: 4,
        overflow: 'hidden',
        // Whatever the change needs. A long before/after pair makes the panel
        // taller and the rail scrolls to cover it, rather than the panel
        // clipping its own body behind a second scrollbar.
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.75,
          flexShrink: 0,
          backgroundColor: theme.palette.grey[200],
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            // Number, label and type chip wrap onto a second line in a narrow
            // rail instead of squeezing the chip out of the panel.
            flexWrap: 'wrap',
            mb: primaryRef ? 1 : 0,
          }}
        >
          <Box
            sx={{
              width: 26,
              height: 26,
              borderRadius: '50%',
              flexShrink: 0,
              border: `1.5px solid ${toneColor}`,
              backgroundColor: theme.palette.background.paper,
              color: toneColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11.5,
              fontWeight: 700,
            }}
          >
            {index}
          </Box>
          <Typography
            sx={{
              fontSize: 11.5,
              fontWeight: 600,
              color: 'text.tertiary',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {t('app.remediation.review.changeLabel', { n: index })}
          </Typography>
          <Chip
            size="small"
            label={t(`app.remediation.review.change.${change.type}` as SingularKeys)}
            sx={{ height: 20, fontSize: 11, fontWeight: 600, color: toneColor }}
          />
        </Box>

        {primaryRef && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <QuotesIcon size={13} color={theme.palette.primary.main} style={{ marginTop: 3 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: 'primary.main',
                  // Clause labels have no spaces to break on ("ECC-1-2-3-P-1"),
                  // and the rail is narrow enough for that to matter.
                  overflowWrap: 'anywhere',
                }}
              >
                {primaryRef.label}
              </Typography>
              {primaryRef.excerpt && (
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25 }}>
                  {primaryRef.excerpt}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ px: 2, pt: 2 }}>
        {change.containsPlaceholders && (
          <Box
            sx={{
              backgroundColor: theme.palette.ocean[100],
              border: `1px solid ${theme.palette.ocean[300]}`,
              borderRadius: 2.5,
              px: 1.5,
              py: 1.25,
              mb: 1.75,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <SparkleIcon size={12} color={theme.palette.ocean[700]} />
              <Typography
                sx={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: theme.palette.ocean[700],
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {t('app.remediation.review.placeholders')}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 12.5, lineHeight: 1.6 }}>
              {t('app.remediation.review.placeholdersHint')}
            </Typography>
          </Box>
        )}

        {prose && change.before && (
          <BeforeAfterBlock
            label={t('app.remediation.review.before')}
            text={change.before}
            color={theme.palette.error.main}
            struck
          />
        )}
        {prose && change.after && (
          <BeforeAfterBlock
            label={t('app.remediation.review.after')}
            text={change.after}
            color={theme.palette.green[800]}
          />
        )}
        {!prose && (
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
            {change.header} · {change.columnKey}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 2,
          flexShrink: 0,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        {/* `minWidth: 0` on both: a narrow rail shrinks the pair evenly rather
            than letting the longer label set a floor and overflow the panel. */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            fullWidth
            variant={accepted ? 'primary' : 'secondary'}
            disabled={busy}
            startIcon={<CheckIcon size={15} />}
            sx={{ minWidth: 0, px: 1 }}
            onClick={() => onDecide('ACCEPTED')}
          >
            {t(accepted ? 'app.remediation.review.accepted' : 'app.remediation.review.accept')}
          </Button>
          <Button
            fullWidth
            variant={rejected ? 'primary' : 'secondary'}
            disabled={busy}
            startIcon={<XIcon size={14} />}
            sx={{ minWidth: 0, px: 1 }}
            onClick={() => onDecide('REJECTED')}
          >
            {t(rejected ? 'app.remediation.review.rejected' : 'app.remediation.review.reject')}
          </Button>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mt: 1.5,
            pt: 1.5,
            borderTop: `1px solid ${theme.palette.grey[200]}`,
          }}
        >
          <Button
            variant="tertiary"
            disabled={!hasPrev}
            onClick={() => onNav('prev')}
            startIcon={<CaretLeftIcon size={13} />}
            sx={{ fontSize: 12, minWidth: 0, px: 0.5 }}
          >
            {t('app.remediation.review.prev')}
          </Button>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {['A', 'R'].map((k) => (
              <Box
                key={k}
                component="kbd"
                sx={{
                  backgroundColor: theme.palette.grey[200],
                  px: 0.75,
                  borderRadius: 1,
                  fontSize: 10,
                }}
              >
                {k}
              </Box>
            ))}
          </Box>
          <Button
            variant="tertiary"
            disabled={!hasNext}
            onClick={() => onNav('next')}
            endIcon={<CaretRightIcon size={13} />}
            sx={{ fontSize: 12, minWidth: 0, px: 0.5 }}
          >
            {t('app.remediation.review.next')}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
});

const BeforeAfterBlock: FC<{
  label: string;
  text: string;
  color: string;
  struck?: boolean;
}> = observer(({ label, text, color, struck }) => (
  <Box sx={{ mb: 1 }}>
    <Typography
      sx={{
        fontSize: 10.5,
        fontWeight: 700,
        color,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        mb: 0.5,
      }}
    >
      {label}
    </Typography>
    <Markdown
      sx={{
        backgroundColor: struck ? 'rgba(232,71,73,0.06)' : 'rgba(27,158,92,0.06)',
        border: `1px solid ${struck ? 'rgba(232,71,73,0.20)' : 'rgba(27,158,92,0.20)'}`,
        borderRadius: 2,
        px: 1.25,
        py: 1,
        fontSize: 12.5,
        lineHeight: 1.6,
        // Quoted document text can carry long unbroken runs — a URL, a token,
        // an un-spaced reference — and the rail is too narrow to survive one.
        overflowWrap: 'anywhere',
        color: struck ? color : 'text.primary',
        textDecoration: struck ? 'line-through' : 'none',
      }}
    >
      {text}
    </Markdown>
  </Box>
));

/** Filter tabs + the compact list of every change in the document. */
export const ChangeList: FC<{
  changes: AnyChange[];
  order: string[];
  selectedId: string | null;
  filter: ChangeFilter;
  onFilter: (f: ChangeFilter) => void;
  onSelect: (id: string) => void;
}> = observer(({ changes, order, selectedId, filter, onFilter, onSelect }) => {
  const theme = useTheme();
  const { t } = useTrans();

  const [page, setPage] = useState(1);

  const accepted = changes.filter((c) => c.decision === RemediationDecision.ACCEPTED).length;
  const rejected = changes.filter((c) => c.decision === RemediationDecision.REJECTED).length;
  const pending = changes.length - accepted - rejected;

  const counts: Record<ChangeFilter, number> = {
    all: changes.length,
    pending,
    accepted,
    rejected,
  };
  // Memoized so the page-follows-selection effect below keys off the rows
  // themselves rather than off a new array every render.
  const visible = useMemo(
    () =>
      changes.filter(
        (c) =>
          filter === 'all' ||
          (filter === 'pending' && c.decision === RemediationDecision.PENDING) ||
          (filter === 'accepted' && c.decision === RemediationDecision.ACCEPTED) ||
          (filter === 'rejected' && c.decision === RemediationDecision.REJECTED),
      ),
    [changes, filter],
  );

  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));

  // The page follows the selection, because the selection moves from places the
  // list does not control: the prev/next buttons, the A/R/arrow shortcuts and
  // the marks in the document itself. Without this, walking the change set with
  // the keyboard leaves the list showing a page the reviewer left long ago.
  //
  // Only when the selection actually *moves*, though. The rows are rebuilt on
  // every decision and on every poll while the run is still generating, and a
  // reviewer paging ahead to see what is coming should not be dragged back by a
  // refetch. A page that no longer exists is the one thing always corrected.
  const followed = useRef<string | null>(null);
  useEffect(() => {
    const moved = selectedId !== followed.current;
    followed.current = selectedId;
    const i = moved ? visible.findIndex((c) => c.changeId === selectedId) : -1;
    // `-1` also covers a change that is not in this filter's rows — deciding one
    // under the "pending" filter drops it out of the list.
    if (i < 0) {
      setPage((p) => Math.min(p, totalPages));
      return;
    }
    setPage(Math.floor(i / PAGE_SIZE) + 1);
  }, [selectedId, visible, totalPages]);

  const from = (page - 1) * PAGE_SIZE;
  const rows = visible.slice(from, from + PAGE_SIZE);

  const FILTERS: ChangeFilter[] = ['all', 'pending', 'accepted', 'rejected'];

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.grey[300]}`,
        // Every row, at full height: the rail is the scrollport now, so the list
        // has no reason to hold a second one.
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Sticky against the rail's scroll: the counts and the filter are how a
          reviewer navigates a long list, and scrolling into the list is exactly
          when they stop being reachable. */}
      <Box
        sx={{
          px: 2,
          pt: 1.5,
          pb: 1.25,
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}
        >
          <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
            {t('app.remediation.review.changes')}{' '}
            <Box component="span" sx={{ color: 'text.tertiary', fontWeight: 400 }}>
              · {changes.length}
            </Box>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, fontSize: 11, color: 'text.tertiary' }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
              <CheckIcon size={11} color={theme.palette.green[700]} />
              {accepted}
            </Box>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
              <XIcon size={11} color={theme.palette.text.tertiary} />
              {rejected}
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {FILTERS.map((f) => {
            const active = filter === f;
            return (
              <Box
                key={f}
                component="button"
                // Back to the first page: a filter change is a new list, and
                // "page 3" of the old one means nothing in it. The effect above
                // still wins if the selected change lives further in.
                onClick={() => {
                  onFilter(f);
                  setPage(1);
                }}
                sx={{
                  // `1 1 0` with `minWidth: 0`: four equal tabs that keep
                  // shrinking past their labels rather than pushing the row
                  // wider than the rail. The count is the part worth keeping,
                  // so the word is what gets clipped.
                  flex: '1 1 0',
                  minWidth: 0,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 11,
                  fontWeight: 600,
                  py: 0.6,
                  px: 0.5,
                  borderRadius: 1.75,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  backgroundColor: active ? theme.palette.text.primary : theme.palette.grey[200],
                  color: active ? theme.palette.common.white : theme.palette.text.secondary,
                }}
              >
                {t(`app.remediation.review.filter.${f}` as SingularKeys)} {counts[f]}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box>
        {rows.length === 0 ? (
          <Typography sx={{ py: 4, textAlign: 'center', color: 'text.tertiary', fontSize: 12.5 }}>
            —
          </Typography>
        ) : (
          rows.map((change) => {
            const selected = selectedId === change.changeId;
            const preview = isProseChange(change)
              ? ((change.type === 'deletion' ? change.before : change.after) ?? '')
              : `${change.header} · ${change.columnKey}`;
            return (
              <Box
                key={change.changeId}
                component="button"
                onClick={() => onSelect(change.changeId)}
                sx={{
                  width: '100%',
                  display: 'block',
                  textAlign: 'start',
                  px: 1.75,
                  py: 1.25,
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: selected ? theme.palette.ocean[100] : 'transparent',
                  borderInlineStart: `3px solid ${
                    selected ? theme.palette.ocean[700] : 'transparent'
                  }`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Pin
                    n={order.indexOf(change.changeId) + 1}
                    decision={change.decision}
                    selected={selected}
                    onClick={() => onSelect(change.changeId)}
                  />
                  <Typography
                    // `minWidth: 0` is what makes `noWrap` truncate here: as a
                    // flex item it would otherwise refuse to shrink and push the
                    // decision icon off the row.
                    sx={{ fontSize: 11, color: 'primary.main', fontWeight: 600, minWidth: 0 }}
                    noWrap
                  >
                    {change.references[0]?.label ??
                      t(`app.remediation.review.change.${change.type}` as SingularKeys)}
                  </Typography>
                  <Box sx={{ marginInlineStart: 'auto', display: 'flex' }}>
                    {change.decision === RemediationDecision.ACCEPTED ? (
                      <CheckCircleIcon size={14} color={theme.palette.green[700]} />
                    ) : change.decision === RemediationDecision.REJECTED ? (
                      <XCircleIcon size={14} color={theme.palette.text.tertiary} />
                    ) : (
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: theme.palette.warning.main,
                        }}
                      />
                    )}
                  </Box>
                </Box>
                <Typography
                  component="div"
                  sx={{
                    fontSize: 11.5,
                    color: 'text.secondary',
                    lineHeight: 1.45,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  <Markdown inline>{preview}</Markdown>
                </Typography>
              </Box>
            );
          })
        )}
      </Box>

      {/* Sticky like the header above it: paging is the one control that has to
          stay reachable from inside a list, and the rail scrolls as one piece.
          Hidden entirely at a single page — a pager that can only say "1 of 1"
          is noise in a panel this dense. */}
      {totalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: 1.25,
            py: 0.75,
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            backgroundColor: theme.palette.background.paper,
            borderTop: `1px solid ${theme.palette.grey[300]}`,
          }}
        >
          <IconButton
            size="small"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label={t('app.common.previous')}
          >
            <CaretLeftIcon size={14} />
          </IconButton>
          {/* The range, not the page number: "11–20 of 37" answers where the
              reviewer is in the change set, which is what they are counting. */}
          <Typography sx={{ fontSize: 11, color: 'text.tertiary', fontWeight: 500 }}>
            {t('app.remediation.review.pageRange', {
              from: from + 1,
              to: from + rows.length,
              total: visible.length,
            })}
          </Typography>
          <IconButton
            size="small"
            disabled={page === totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            aria-label={t('app.common.next')}
          >
            <CaretRightIcon size={14} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
});
